"use client";

// URL state management for tabs using nuqs
import { useQueryState } from "nuqs";
import { startTransition, useMemo, useState } from "react";
import { ArtistContentTabs } from "@/components/features/artist/tabs/artist-content-tabs";
import { ArtistFestivalsView } from "@/components/features/artist/views/artist-festivals-view";
import { ArtistSetsView } from "@/components/features/artist/views/artist-sets-view";
import { ArtistShowsView } from "@/components/features/artist/views/artist-shows-view";
import { ProfileYearSelector } from "@/components/features/profile/tabs/profile-year-selector";
import { EnhancedSearchInput } from "@/components/shared/filters/search/enhanced-search-input";
import {
	type SortOption,
	UniversalSortDropdown,
} from "@/components/shared/filters/search/universal-sort-dropdown";
import { useSearchInput } from "@/hooks/shared/use-search-input";
import type { SetFacepileData } from "@/lib/data/shared/facepile-cached";
import type { FestivalFacepileData } from "@/lib/data/shared/facepile-cached";
import type { ShowFacepileData } from "@/lib/data/shared/facepile-cached";
import { trpc } from "@/lib/trpc/react";
import {
	artistContentTypeParser,
	profileYearParser,
} from "@/lib/utils/url-parsers";
import { ArtistErrorBoundaryWrapper } from "../error-boundary/";
import { ArtistContentSkeleton } from "./artist-content-skeleton";

function filterByTime<T>(
	items: T[],
	getDateStr: (item: T) => string | undefined,
	mode: "upcoming" | "past",
): T[] {
	const now = new Date();
	now.setHours(0, 0, 0, 0);
	return items.filter((item) => {
		const dateStr = getDateStr(item);
		if (!dateStr) return false;
		const parts = dateStr.split("-");
		const date = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
		date.setHours(23, 59, 59, 999);
		return mode === "upcoming" ? date >= now : date < now;
	});
}

interface ArtistContentProps {
	artistId: string;
	artistName: string;
	artistSlug: string;
}

// Main component
const ArtistContentComponent = ({
	artistId,
	artistName,
}: ArtistContentProps) => {
	// URL state for content type and year management using nuqs
	const [activeContentType, setActiveContentType] = useQueryState(
		"type",
		artistContentTypeParser,
	);
	const [selectedYear, setSelectedYear] = useQueryState(
		"year",
		profileYearParser,
	);

	// Search states for each tab using useSearchInput hook
	const festivalsSearch = useSearchInput({ debounceMs: 300 });
	const setsSearch = useSearchInput({ debounceMs: 300 });
	const showsSearch = useSearchInput({ debounceMs: 300 });

	// Standardized sort state for all tabs using UniversalSortDropdown
	const [festivalsSort, setFestivalsSort] = useState("all");
	const [setsSort, setSetsSort] = useState("all");
	const [showsSort, setShowsSort] = useState("all");

	// Sort options for each tab (shared structure)
	const buildSortOptions = (allLabel: string): SortOption[] => [
		{ value: "all", label: allLabel },
		{ value: "upcoming", label: "Upcoming" },
		{ value: "past", label: "Past" },
	];
	const festivalsSortOptions = buildSortOptions("All Festivals");
	const setsSortOptions = buildSortOptions("All Sets");
	const showsSortOptions = buildSortOptions("All Shows");

	// Helper functions to parse sort values
	const parseSortValue = (sortValue: string) => {
		// Handle time-based filter values for festivals, sets, and shows
		if (
			sortValue === "all" ||
			sortValue === "upcoming" ||
			sortValue === "past"
		) {
			return {
				sortBy: "date",
				sortAscending: false, // Default to upcoming first (desc)
			};
		}

		const parts = sortValue.split("-");
		const direction = parts[parts.length - 1];
		const type = parts.slice(0, -1).join("-");
		return {
			sortBy: type,
			sortAscending: direction === "asc",
		};
	};

	// Parse sort values for each tab
	const festivalsSortParsed = parseSortValue(festivalsSort);
	const setsSortParsed = parseSortValue(setsSort);
	const showsSortParsed = parseSortValue(showsSort);

	// Conditional data loading (exact pattern from profile)
	const festivalsEnabled = activeContentType === "festivals";
	const {
		data: festivalsData,
		isLoading: festivalsLoading,
		error: festivalsError,
		hasNextPage: festivalsHasNextPage,
		fetchNextPage: festivalsFetchNextPage,
		isFetchingNextPage: festivalsIsFetchingNextPage,
	} = trpc.artist.getFestivals.useInfiniteQuery(
		{
			artistId,
			year: selectedYear,
			search: festivalsSearch.debouncedValue,
			sortBy: "date",
			sortAscending: festivalsSortParsed.sortAscending,
			limit: 12,
		},
		{
			enabled: festivalsEnabled && !!artistId,
			getNextPageParam: (lastPage) => lastPage.nextCursor,
			staleTime: 2 * 60 * 1000, // 2 minutes - festivals change less frequently
			gcTime: 15 * 60 * 1000, // 15 minutes - keep in memory longer
			refetchOnWindowFocus: false, // Prevent refetch on tab switch
		},
	);

	const setsEnabled = activeContentType === "sets";
	const {
		data: setsData,
		isLoading: setsLoading,
		error: setsError,
		hasNextPage: setsHasNextPage,
		fetchNextPage: setsFetchNextPage,
		isFetchingNextPage: setsIsFetchingNextPage,
	} = trpc.artist.getSets.useInfiniteQuery(
		{
			artistId,
			year: selectedYear,
			search: setsSearch.debouncedValue,
			sortBy: "date",
			sortAscending: setsSortParsed.sortAscending,
			limit: 12,
		},
		{
			enabled: setsEnabled && !!artistId,
			getNextPageParam: (lastPage) => lastPage.nextCursor,
			staleTime: 1 * 60 * 1000, // 1 minute - sets change more frequently
			gcTime: 10 * 60 * 1000, // 10 minutes
			refetchOnWindowFocus: false,
		},
	);

	const showsEnabled = activeContentType === "shows";
	const {
		data: showsData,
		isLoading: showsLoading,
		error: showsError,
		hasNextPage: showsHasNextPage,
		fetchNextPage: showsFetchNextPage,
		isFetchingNextPage: showsIsFetchingNextPage,
	} = trpc.artist.getShows.useInfiniteQuery(
		{
			artistId,
			year: selectedYear,
			search: showsSearch.debouncedValue,
			sortBy: "date",
			sortAscending: showsSortParsed.sortAscending,
			limit: 8,
		},
		{
			enabled: showsEnabled && !!artistId,
			getNextPageParam: (lastPage) => lastPage.nextCursor,
			staleTime: 3 * 60 * 1000, // 3 minutes - shows are somewhat stable
			gcTime: 12 * 60 * 1000, // 12 minutes
			refetchOnWindowFocus: false,
		},
	);

	// Get available years from server based on artist's actual data
	const { data: availableYearsData } = trpc.artist.getAvailableYears.useQuery(
		{
			artistId,
			contentType: activeContentType as "festivals" | "sets" | "shows",
		},
		{
			enabled: !!artistId,
			staleTime: 5 * 60 * 1000, // 5 minutes
		},
	);

	// Get actual counts from server
	const { data: countsData } = trpc.artist.getCounts.useQuery(
		{
			artistId,
		},
		{
			enabled: !!artistId,
			staleTime: 1 * 60 * 1000, // 1 minute
		},
	);

	const availableYears =
		availableYearsData?.years.map((year) => parseInt(year, 10)) || [];

	// Extract data from tRPC responses - memoized with time filtering
	const festivalsArray = useMemo(() => {
		const allFestivals =
			festivalsData?.pages?.flatMap((page) => page.items) || [];

		if (festivalsSort === "upcoming" || festivalsSort === "past") {
			return filterByTime(allFestivals, (f) => f?.end_date, festivalsSort);
		}
		return allFestivals;
	}, [festivalsData, festivalsSort]);

	// Extract facepile data for festivals
	const festivalsFacepileData = useMemo(() => {
		if (!festivalsData?.pages) return {};

		// Merge facepile data from all pages
		const mergedData: FestivalFacepileData = {};
		festivalsData.pages.forEach((page) => {
			if (page.facepileData) {
				Object.assign(mergedData, page.facepileData);
			}
		});

		return mergedData;
	}, [festivalsData]);

	const setsArray = useMemo(() => {
		const allSets =
			setsData?.pages?.flatMap((page) =>
				page.items
					.filter((item) => item.artist !== null) // Filter out sets without artists
					.map((item) => ({
						...item,
						artist: item.artist!, // Now we know it's not null
						friends_set: item.friends_set ?? undefined,
						is_collaborative: item.is_collaborative ?? undefined,
						is_group_alias: item.is_group_alias ?? undefined,
						display_format:
							(item.display_format as
								| "B2B"
								| "x"
								| "+"
								| "F2F"
								| "&"
								| "vs"
								| "B3B"
								| "with"
								| null) ?? undefined,
					})),
			) || [];

		if (setsSort === "upcoming" || setsSort === "past") {
			return filterByTime(allSets, (s) => (s?.festival as { end_date?: string } | undefined)?.end_date, setsSort);
		}
		return allSets;
	}, [setsData, setsSort]);

	// Extract facepile data for sets
	const setsFacepileData = useMemo(() => {
		if (!setsData?.pages) return {};

		// Merge facepile data from all pages
		const mergedData: SetFacepileData = {};
		setsData.pages.forEach((page) => {
			if (page.facepileData) {
				Object.assign(mergedData, page.facepileData);
			}
		});

		return mergedData;
	}, [setsData]);

	const showsArray = useMemo(() => {
		const allShows = showsData?.pages?.flatMap((page) => page.items) || [];

		if (showsSort === "upcoming" || showsSort === "past") {
			return filterByTime(allShows, (s) => s?.date, showsSort);
		}
		return allShows;
	}, [showsData, showsSort]);

	// Extract facepile data for shows
	const showsFacepileData = useMemo(() => {
		if (!showsData?.pages) return {};

		// Merge facepile data from all pages
		const mergedData: ShowFacepileData = {};
		showsData.pages.forEach((page) => {
			if (page.facepileData) {
				Object.assign(mergedData, page.facepileData);
			}
		});

		return mergedData;
	}, [showsData]);

	// Calculate counts for tabs
	const counts = useMemo(() => {
		// Use server-provided counts if available, otherwise fall back to loaded data
		if (countsData) {
			return countsData;
		}
		// Fallback to loaded data length
		return {
			festivals: festivalsArray.length,
			sets: setsArray.length,
			shows: showsArray.length,
		};
	}, [countsData, festivalsArray.length, setsArray.length, showsArray.length]);

	// Event handlers
	const handleContentTypeChange = (value: string) => {
		startTransition(() => {
			setActiveContentType(value as "festivals" | "sets" | "shows");
			// Reset year filter when changing content type
			setSelectedYear("all");
		});
	};

	const handleYearChange = (value: string) => {
		startTransition(() => {
			setSelectedYear(value);
		});
	};

	// Loading state - basic check for initial data
	if (!artistId) {
		return <ArtistContentSkeleton />;
	}

	return (
		<div className="flex flex-1 flex-col">
			{/* Artist header would go here if needed */}

			{/* Tab Navigation */}
			<ArtistContentTabs
				activeContentType={
					(activeContentType || "festivals") as "festivals" | "sets" | "shows"
				}
				onContentTypeChange={handleContentTypeChange}
				counts={counts}
			/>

			<div>
				{activeContentType === "festivals" && (
					<StickyFilterRow
						selectedYear={selectedYear}
						onYearChange={handleYearChange}
						availableYears={availableYears.length > 0 ? availableYears : undefined}
						sortValue={festivalsSort}
						onSortChange={setFestivalsSort}
						sortOptions={festivalsSortOptions}
						searchPlaceholder="Search festivals..."
						search={festivalsSearch}
					/>
				)}
				{activeContentType === "sets" && (
					<StickyFilterRow
						selectedYear={selectedYear}
						onYearChange={handleYearChange}
						availableYears={availableYears.length > 0 ? availableYears : undefined}
						sortValue={setsSort}
						onSortChange={setSetsSort}
						sortOptions={setsSortOptions}
						searchPlaceholder="Search by festival, stage, or artist..."
						search={setsSearch}
					/>
				)}
				{activeContentType === "shows" && (
					<StickyFilterRow
						selectedYear={selectedYear}
						onYearChange={handleYearChange}
						availableYears={availableYears.length > 0 ? availableYears : undefined}
						sortValue={showsSort}
						onSortChange={setShowsSort}
						sortOptions={showsSortOptions}
						searchPlaceholder="Search shows or venues..."
						search={showsSearch}
					/>
				)}

				{/* Content based on active tab */}
				{activeContentType === "festivals" && (
					<ArtistErrorBoundaryWrapper>
						{festivalsLoading ? (
							<ArtistContentSkeleton type="festivals" />
						) : festivalsError ? (
							<div className="flex items-center justify-center py-8">
								<div className="text-sm text-red-500">
									Error loading festivals: {festivalsError.message}
								</div>
							</div>
						) : (
							<ArtistFestivalsView
								festivals={festivalsArray}
								artistId={artistId}
								artistName={artistName}
								fetchNextPage={festivalsFetchNextPage}
								hasNextPage={festivalsHasNextPage}
								isFetchingNextPage={festivalsIsFetchingNextPage}
								selectedYear={selectedYear}
								searchTerm={festivalsSearch.debouncedValue}
								sortBy={festivalsSort}
								onClearSearch={() => festivalsSearch.setValue("")}
								onClearYear={() => setSelectedYear("all")}
								isActive={activeContentType === "festivals"}
								facepileData={festivalsFacepileData}
							/>
						)}
					</ArtistErrorBoundaryWrapper>
				)}

				{/* Sets View */}
				{activeContentType === "sets" && (
					<ArtistErrorBoundaryWrapper>
						{setsLoading ? (
							<ArtistContentSkeleton type="sets" />
						) : setsError ? (
							<div className="flex items-center justify-center py-8">
								<div className="text-sm text-red-500">
									Error loading sets: {setsError.message}
								</div>
							</div>
						) : (
							<ArtistSetsView
								sets={setsArray}
								artistName={artistName}
								fetchNextPage={setsFetchNextPage}
								hasNextPage={setsHasNextPage}
								isFetchingNextPage={setsIsFetchingNextPage}
								selectedYear={selectedYear}
								searchTerm={setsSearch.debouncedValue}
								sortBy={setsSort}
								onClearSearch={() => setsSearch.setValue("")}
								onClearYear={() => setSelectedYear("all")}
								isActive={activeContentType === "sets"}
								facepileData={setsFacepileData}
							/>
						)}
					</ArtistErrorBoundaryWrapper>
				)}

				{/* Shows View */}
				{activeContentType === "shows" && (
					<ArtistErrorBoundaryWrapper>
						{showsLoading ? (
							<ArtistContentSkeleton type="shows" />
						) : showsError ? (
							<div className="flex items-center justify-center py-8">
								<div className="text-sm text-red-500">
									Error loading shows: {showsError.message}
								</div>
							</div>
						) : (
							<ArtistShowsView
								shows={showsArray}
								artistId={artistId}
								artistName={artistName}
								fetchNextPage={showsFetchNextPage}
								hasNextPage={showsHasNextPage}
								isFetchingNextPage={showsIsFetchingNextPage}
								selectedYear={selectedYear}
								searchTerm={showsSearch.debouncedValue}
								sortBy={showsSort}
								onClearSearch={() => showsSearch.setValue("")}
								onClearYear={() => setSelectedYear("all")}
								isActive={activeContentType === "shows"}
								facepileData={showsFacepileData}
							/>
						)}
					</ArtistErrorBoundaryWrapper>
				)}
			</div>
		</div>
	);
};

ArtistContentComponent.displayName = "ArtistContentComponent";

export const ArtistContent = ArtistContentComponent;

function StickyFilterRow({
	selectedYear,
	onYearChange,
	availableYears,
	sortValue,
	onSortChange,
	sortOptions,
	searchPlaceholder,
	search,
}: {
	selectedYear: string | null;
	onYearChange: (value: string) => void;
	availableYears?: number[];
	sortValue: string;
	onSortChange: (value: string) => void;
	sortOptions: SortOption[];
	searchPlaceholder: string;
	search: ReturnType<typeof useSearchInput>;
}) {
	return (
		<div className="sticky top-[3.5rem] z-30 bg-background pt-3 pb-3 md:top-[calc(3.5rem+env(safe-area-inset-top))]">
			<div className="space-y-3">
				<div className="flex items-center gap-3">
					<ProfileYearSelector
						selectedYear={selectedYear ?? "all"}
						onYearChange={onYearChange}
						availableYears={availableYears}
						className="min-w-0 flex-1"
					/>
					<UniversalSortDropdown
						value={sortValue}
						onValueChange={onSortChange}
						options={sortOptions}
						className="min-w-0 flex-1"
					/>
				</div>
				<EnhancedSearchInput
					placeholder={searchPlaceholder}
					className="flex-1"
					value={search.value}
					onValueChange={(value) => search.setValue(value)}
					showLoadingIndicator={true}
					isLoading={search.isSearching}
				/>
			</div>
		</div>
	);
}
